import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { QuickExit } from "../components/intake/QuickExit";
import { ChatWidget } from "../components/intake/ChatWidget";
import { ProgressStepper } from "../components/intake/ProgressStepper";
import { Stage1Triage } from "../components/intake/Stage1Triage";
import { Stage2Funding } from "../components/intake/Stage2Funding";
import { Stage3Contact } from "../components/intake/Stage3Contact";
import { Stage4RouteSelect } from "../components/intake/Stage4RouteSelect";
import { RouteA } from "../components/intake/RouteA";
import { RouteB } from "../components/intake/RouteB";
import { RouteC } from "../components/intake/RouteC";
import { RouteD } from "../components/intake/RouteD";
import { Confirmation } from "../components/intake/Confirmation";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Save, Lock, RotateCcw } from "lucide-react";
import { INTAKE } from "../constants/testIds";
import { getConfig, createEnquiry, updateEnquiry, submitEnquiry, resumeEnquiry, saveAndReturn, completeLinked, selectRoute } from "../lib/api";
import { parseEntrySource } from "../lib/source";
import { loadDraft, saveDraft, clearDraft } from "../lib/draft";
import { hasAnswers } from "../components/intake/useBackGuard";

const STEP = { TRIAGE: 0, CONTACT: 1, FUNDING: 2, ROUTE: 3, FORM: 4, ROUTEB_CONT: 5, CONFIRM: 6 };

const STEP_NAME = { [STEP.TRIAGE]: "triage", [STEP.FUNDING]: "funding", [STEP.CONTACT]: "contact", [STEP.ROUTE]: "route", [STEP.FORM]: "form" };
const NAME_STEP = { triage: STEP.TRIAGE, funding: STEP.FUNDING, contact: STEP.CONTACT, route: STEP.ROUTE, form: STEP.FORM };

export default function IntakeForm() {
  const [config, setConfig] = useState(null);
  const [enquiryId, setEnquiryId] = useState(null);
  const [platformName, setPlatformName] = useState("our website");
  const [isSocial, setIsSocial] = useState(false);
  const [step, setStep] = useState(STEP.TRIAGE);
  const [form, setForm] = useState({});
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveEmail, setSaveEmail] = useState("");
  const [linkedBId, setLinkedBId] = useState(null);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  // Staff-only testing mode: enable by visiting the form with ?staff in the URL.
  // Persisted in sessionStorage so it survives the ?reset reload.
  const [staffMode] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.has("staff")) sessionStorage.setItem("fcg_staff", "1");
      return sessionStorage.getItem("fcg_staff") === "1";
    } catch {
      return false;
    }
  });

  const resetForm = () => {
    if (!window.confirm("Reset the form for clean testing? This clears the draft saved on this device.")) return;
    clearDraft();
    window.location.href = `${window.location.pathname}?reset`;
  };

  useEffect(() => {
    getConfig().then(setConfig).catch(() => {});
    const params = new URLSearchParams(window.location.search);
    if (params.has("reset")) {
      clearDraft();
      window.history.replaceState({}, "", window.location.pathname);
    }
    const token = params.has("reset") ? null : params.get("resume");
    if (token) {
      resumeEnquiry(token)
        .then((doc) => {
          clearDraft();
          setEnquiryId(doc.id);
          setForm(doc.data || {});
          setPlatformName(doc.platform_name || "our website");
          setIsSocial(!!doc.source?.is_social);
          const savedStep = NAME_STEP[doc.current_step];
          if (savedStep === STEP.FORM && doc.data?.route) {
            setStep(STEP.FORM);
          } else if (savedStep !== undefined) {
            setStep(savedStep);
          } else if (doc.data?.contact) {
            setStep(STEP.ROUTE);
          }
          toast.success("Welcome back. Your saved enquiry has been restored.");
        })
        .catch(() => {
          toast.error("We could not find that saved enquiry. Starting a new one.");
          createEnquiry({ platform_name: platformName }).then((d) => setEnquiryId(d.id));
        });
    } else {
      const draft = params.has("reset") ? null : loadDraft();
      if (draft && draft.enquiryId) {
        setEnquiryId(draft.enquiryId);
        setForm(draft.form || {});
        setPlatformName(draft.platformName || "our website");
        setIsSocial(!!draft.isSocial);
        if (typeof draft.step === "number" && draft.step < STEP.CONFIRM) setStep(draft.step);
        toast.success("We restored your progress on this device.");
      } else {
        const entry = parseEntrySource();
        setPlatformName(entry.platformName);
        setIsSocial(entry.isSocial);
        if (entry.isSocial && (entry.prefill.first_name || entry.prefill.email)) {
          setForm((f) => ({ ...f, contact: { ...(f.contact || {}), ...entry.prefill } }));
        }
        createEnquiry({ platform_name: entry.platformName, source: entry.source })
          .then((d) => setEnquiryId(d.id))
          .catch(() => {});
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!enquiryId) return;
    if (step >= STEP.CONFIRM) { clearDraft(); return; }
    if (step === STEP.ROUTEB_CONT) return;
    saveDraft({ enquiryId, form, step, platformName, isSocial });
  }, [enquiryId, form, step, platformName, isSocial]);

  useEffect(() => {
    if (step === STEP.FORM && enquiryId && form.route) {
      selectRoute(enquiryId, form).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, form.route, enquiryId]);

  const persist = (nextStepName) => {
    if (enquiryId) updateEnquiry(enquiryId, form, nextStepName).catch(() => {});
  };

  const goto = (s, stepName) => {
    persist(stepName);
    setStep(s);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubmit = async () => {
    if (!enquiryId) return;
    setSubmitting(true);
    try {
      await updateEnquiry(enquiryId, form, "submit");
      const res = await submitEnquiry(enquiryId);
      setResult(res);
      const bLink = (res.linked_enquiries || []).find((l) => l.route === "B");
      if (form.route === "A" && form.wants_route_b && bLink) {
        const a = form.routeA || {};
        const carriedB = {
          ...(form.routeB || {}),
          app_status: "Yes",
          about: a.about,
          case_number: a.case_number,
          court_name: a.court_name,
          has_deadline: a.hearing_date ? "Yes" : (form.routeB?.has_deadline),
          deadline_type: a.hearing_date ? "Hearing" : (form.routeB?.deadline_type),
          deadline_date: a.hearing_date,
          deadline_time: a.hearing_time,
          happened: a.what_happened,
          outcome: a.outcome,
          budget: form.budget,
          accessibility: a.accessibility,
        };
        setForm((f) => ({ ...f, routeB: carriedB }));
        setLinkedBId(bLink.id);
        setStep(STEP.ROUTEB_CONT);
      } else {
        setStep(STEP.CONFIRM);
      }
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error("We could not submit your enquiry. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleLinkedBSubmit = async () => {
    if (!linkedBId) return;
    setSubmitting(true);
    try {
      await completeLinked(linkedBId, { route: "B", routeB: form.routeB, contact: form.contact });
      toast.success("Your preparation request has been submitted.");
      setStep(STEP.CONFIRM);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error("We could not submit the preparation request. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const doSaveReturn = async () => {
    if (!enquiryId || !saveEmail) return;
    try {
      await updateEnquiry(enquiryId, form, STEP_NAME[step] || "route");
      await saveAndReturn(enquiryId, saveEmail);
      toast.success("We have emailed you a link to resume this form.");
      setSaveOpen(false);
    } catch (e) {
      toast.error("Could not save. Please try again.");
    }
  };

  const onBackFromRoute = () => {
    if (hasAnswers(form[`route${form.route}`])) {
      toast.success("Your answers are saved. You can switch help type and return any time.");
    }
    goto(STEP.ROUTE, "route");
  };

  const routeProps = { form, set, config, submitting, onBack: onBackFromRoute, onSubmit: handleSubmit };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <QuickExit />
      <ChatWidget />

      <header className="sticky top-0 z-40 border-b border-border bg-white">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <p className="font-serif text-xl text-brand-black leading-none">Family Court Guide</p>
          </div>
          {staffMode && (
            <button
              type="button"
              onClick={resetForm}
              data-testid="staff-reset-form"
              className="inline-flex items-center gap-1.5 text-xs font-medium text-brand-darkgrey border border-border px-3 py-1.5 rounded-sm hover:border-brand-gold hover:text-brand-gold transition-colors"
            >
              <RotateCcw className="h-3.5 w-3.5" strokeWidth={1.75} /> Reset form (staff)
            </button>
          )}
        </div>
        {step < STEP.CONFIRM && (
          <div className="border-t border-border">
            <div className="max-w-3xl mx-auto px-5 sm:px-8 py-3">
              <ProgressStepper current={Math.min(step, 4)} />
            </div>
          </div>
        )}
      </header>

      <main className="flex-1 w-full">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 py-8">
          <div className="bg-white border border-border p-6 sm:p-10">
            <AnimatePresence mode="wait">
              <motion.div
                key={step}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.3, ease: "easeOut" }}
              >
                {step === STEP.TRIAGE && (
                  <Stage1Triage
                    form={form}
                    set={set}
                    enquiryId={enquiryId}
                    onContinue={() => goto(STEP.CONTACT, "contact")}
                  />
                )}
                {step === STEP.CONTACT && (
                  <Stage3Contact form={form} set={set} platformName={platformName} isSocial={isSocial} onBack={() => goto(STEP.TRIAGE, "triage")} onContinue={() => goto(STEP.FUNDING, "funding")} />
                )}
                {step === STEP.FUNDING && (
                  <Stage2Funding form={form} set={set} onBack={() => goto(STEP.CONTACT, "contact")} onContinue={() => goto(STEP.ROUTE, "route")} />
                )}
                {step === STEP.ROUTE && (
                  <Stage4RouteSelect form={form} set={set} onBack={() => goto(STEP.FUNDING, "funding")} onContinue={() => goto(STEP.FORM, "form")} />
                )}
                {step === STEP.FORM && form.route === "A" && <RouteA {...routeProps} />}
                {step === STEP.FORM && form.route === "B" && <RouteB {...routeProps} />}
                {step === STEP.FORM && form.route === "C" && <RouteC {...routeProps} />}
                {step === STEP.FORM && form.route === "D" && <RouteD {...routeProps} />}
                {step === STEP.ROUTEB_CONT && (
                  <RouteB
                    form={form}
                    set={set}
                    config={config}
                    carried
                    submitting={submitting}
                    onSubmit={handleLinkedBSubmit}
                    onSkip={() => setStep(STEP.CONFIRM)}
                  />
                )}
                {step === STEP.CONFIRM && <Confirmation result={result} form={form} config={config} />}
              </motion.div>
            </AnimatePresence>
          </div>

          {step > STEP.TRIAGE && step < STEP.CONFIRM && (
            <div className="mt-4 text-center">
              <button
                type="button"
                onClick={() => setSaveOpen(true)}
                data-testid={INTAKE.saveReturn}
                className="inline-flex items-center gap-2 text-sm text-brand-darkgrey hover:text-brand-gold transition-colors"
              >
                <Save className="h-4 w-4" strokeWidth={1.5} /> Save and return later
              </button>
              <p className="mt-2 text-xs text-muted-foreground" data-testid="autosave-hint">
                Your progress is saved automatically on this device.
              </p>
            </div>
          )}
        </div>
      </main>

      <footer className="border-t border-border bg-white">
        <div className="max-w-3xl mx-auto px-6 py-6 text-xs text-muted-foreground flex flex-wrap gap-4 justify-between">
          <span>© {new Date().getFullYear()} Family Court Guide. This service is privately funded.</span>
          <span className="flex items-center gap-4">
            <a href="/privacy" className="hover:text-brand-gold underline underline-offset-2">Privacy notice</a>
            <a href="/admin/login" aria-label="Staff sign in" title="Staff sign in" className="text-muted-foreground hover:text-brand-gold transition-colors" data-testid="footer-staff-login">
              <Lock className="h-3.5 w-3.5" strokeWidth={1.75} />
            </a>
          </span>
        </div>
      </footer>

      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent className="bg-white rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-serif">Save and return later</DialogTitle>
            <DialogDescription>
              Enter your email address and we will send you a secure link to resume this form.
            </DialogDescription>
          </DialogHeader>
          <Input
            type="email"
            placeholder="you@example.com"
            value={saveEmail}
            onChange={(e) => setSaveEmail(e.target.value)}
            data-testid={INTAKE.saveReturnEmail}
            className="rounded-sm focus-visible:ring-brand-gold"
          />
          <DialogFooter>
            <Button onClick={doSaveReturn} disabled={!saveEmail} data-testid={INTAKE.saveReturnSend} className="bg-brand-black text-white hover:bg-brand-gold rounded-sm">
              Email me a link
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
